import { produce } from 'immer'
import {
  GET_CREDIT_CARD_BULK_TEMPLATE_FAILURE,
  GET_CREDIT_CARD_BULK_TEMPLATE_INIT,
  GET_CREDIT_CARD_BULK_TEMPLATE_SUCCESS,
  GetCreditCardBulkTemplateActions,
  SET_RECEIPT_ATTACHMENT_IDS,
  SetReceiptAttachmentIdsAction,
} from 'src/app/CreateVirtualCardForm/redux/createVirtualCardForm.actions'
import { isHandledPaywall402 } from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import {
  createBillPayRouteProps,
  createVirtualCardRouteProps,
} from 'src/navigation/routeProps'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions,
} from 'src/queries/receiptAttachments/receiptAttachments.events'
import {
  GET_CREDIT_CARDS_FAILURE,
  GET_CREDIT_CARDS_INIT,
  GET_CREDIT_CARDS_SUCCESS,
} from 'src/redux/actions/data/creditCards.actions'
import {
  POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_FAILURE,
  POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_INIT,
  POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_SUCCESS,
  PostCreditCardBulkVirtualCardsActions,
} from 'src/redux/actions/data/virtualCardPushTasks.actions'
import {
  POST_BULK_VIRTUAL_CARD_FAILURE,
  POST_VIRTUAL_CARD_FAILURE,
  POST_VIRTUAL_CARD_INIT,
  POST_VIRTUAL_CARD_SUCCESS,
  PostBulkVirtualCardActions,
  PostVirtualCardActions,
} from 'src/redux/actions/data/virtualCards.actions'
import CreditCardsFullPaginatedDataReducer, {
  Actions as CreditCardsFullPaginatedDataActions,
  CreditCardsFullPaginatedDataState,
  initialState as initialCreditCardsFullPaginatedDataState,
} from 'src/redux/creditCardsFullPaginatedData.reducer'
import { createFlyoutReducer } from 'src/redux/genericReducers'
import RecentUsersFullPaginatedDataReducer, {
  Actions as RecentUsersFullPaginatedDataActions,
  initialState as initialRecentUsersFullPaginatedDataState,
  RecentUsersFullPaginatedDataState,
} from 'src/redux/recentUsersFullPaginatedData.reducer'
import UsersFullPaginatedDataReducer, {
  Actions as UsersFullPaginatedDataActions,
  initialState as initialUsersFullPaginatedDataState,
  UsersFullPaginatedDataState,
} from 'src/redux/usersFullPaginatedData.reducer'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface CreateVirtualCardBaseState {
  receiptAttachmentIds: string[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  bulkTemplate?: string
  loadingBulkTemplate: boolean
  templateType?: string
  taskIds?: string[]
  invalidEmails?: string[]
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  postingCreditCardBulkVirtualCards: boolean
  multipleTopLevelError?: string
  multipleErrorDetailMap?: ErrorDetailMap<string>
}

export const initialBaseState: CreateVirtualCardBaseState = {
  receiptAttachmentIds: [],
  uploadingAttachmentThumbnails: {},
  postingVirtualCard: false,
  loadingBulkTemplate: false,
  postingCreditCardBulkVirtualCards: false,
}

export type BaseActions =
  | UploadAttachmentActions
  | PostVirtualCardActions
  | GetCreditCardBulkTemplateActions
  | PostCreditCardBulkVirtualCardsActions
  | SetReceiptAttachmentIdsAction
  | PostBulkVirtualCardActions

const BaseCreateVirtualCardReducer = (
  state = initialBaseState,
  action: BaseActions,
): CreateVirtualCardBaseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case SET_RECEIPT_ATTACHMENT_IDS: {
        draft.receiptAttachmentIds = action.payload.ids
        return draft
      }

      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.receiptAttachmentIds = [
          ...draft.receiptAttachmentIds,
          action.payload.id,
        ]
        return draft
      }

      case POST_VIRTUAL_CARD_INIT: {
        draft.postingVirtualCard = true
        draft.topLevelError = undefined
        draft.errorDetailMap = undefined
        return draft
      }

      case POST_VIRTUAL_CARD_SUCCESS: {
        draft.postingVirtualCard = false
        return draft
      }

      case POST_BULK_VIRTUAL_CARD_FAILURE:
      case POST_VIRTUAL_CARD_FAILURE: {
        draft.postingVirtualCard = false
        if (isHandledPaywall402(action.payload)) {
          return draft
        }
        const errors = parseBackendErrors(action.payload)
        draft.topLevelError = errors.topLevelError
        draft.errorDetailMap = errors.errorDetailMap
        return draft
      }

      case GET_CREDIT_CARD_BULK_TEMPLATE_INIT: {
        draft.bulkTemplate = undefined
        draft.loadingBulkTemplate = true
        return draft
      }

      case GET_CREDIT_CARD_BULK_TEMPLATE_FAILURE: {
        draft.loadingBulkTemplate = false
        return draft
      }

      case GET_CREDIT_CARD_BULK_TEMPLATE_SUCCESS: {
        draft.bulkTemplate = action.payload
        draft.loadingBulkTemplate = false
        draft.templateType = action.templateType
        return draft
      }

      case POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_INIT: {
        draft.postingCreditCardBulkVirtualCards = true
        draft.multipleTopLevelError = undefined
        draft.multipleErrorDetailMap = undefined
        draft.invalidEmails = undefined
        return draft
      }

      case POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_FAILURE: {
        draft.postingCreditCardBulkVirtualCards = false
        if (isHandledPaywall402(action.payload)) {
          return draft
        }
        const errors = parseBackendErrors(action.payload)
        draft.multipleErrorDetailMap = errors.errorDetailMap
        draft.multipleTopLevelError =
          errors.errorDetailMap === undefined
            ? action.payload.details?.[0].error || errors.topLevelError
            : errors.topLevelError
        return draft
      }

      case POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_SUCCESS: {
        draft.postingCreditCardBulkVirtualCards = false
        draft.taskIds = action.payload.bulkVirtualCardPush.tasks.map(
          (task) => task.taskId,
        )
        draft.invalidEmails = action.payload.invalidEmails
        return draft
      }
    }
  })
}

export type CreateVirtualCardState = CreateVirtualCardBaseState & {
  creditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  recurringEnabledCreditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  guestAccessEnabledCreditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  balanceLockedEnabledCreditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  usersFullPaginatedData: UsersFullPaginatedDataState
  recentUsersFullPaginatedData: RecentUsersFullPaginatedDataState
}

export const initialState: CreateVirtualCardState = {
  ...initialBaseState,
  creditCardsFullPaginatedData: initialCreditCardsFullPaginatedDataState,
  recurringEnabledCreditCardsFullPaginatedData:
    initialCreditCardsFullPaginatedDataState,
  guestAccessEnabledCreditCardsFullPaginatedData:
    initialCreditCardsFullPaginatedDataState,
  balanceLockedEnabledCreditCardsFullPaginatedData:
    initialCreditCardsFullPaginatedDataState,
  usersFullPaginatedData: initialUsersFullPaginatedDataState,
  recentUsersFullPaginatedData: initialRecentUsersFullPaginatedDataState,
}

export type Actions =
  | BaseActions
  | CreditCardsFullPaginatedDataActions
  | UsersFullPaginatedDataActions
  | RecentUsersFullPaginatedDataActions

export const CoreCreateVirtualCardFormReducer = (
  state = initialState,
  action: Actions,
): CreateVirtualCardState => ({
  ...BaseCreateVirtualCardReducer(state, action as BaseActions),
  creditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    (action.payload.queryParameters.recurrenceEnabled ||
      action.payload.queryParameters.directEnabled ||
      action.payload.queryParameters.balanceLockedEnabled)
      ? state.creditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.creditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
  recurringEnabledCreditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    !action.payload.queryParameters.recurrenceEnabled
      ? state.recurringEnabledCreditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.recurringEnabledCreditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
  guestAccessEnabledCreditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    !action.payload.queryParameters.directEnabled
      ? state.guestAccessEnabledCreditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.guestAccessEnabledCreditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
  balanceLockedEnabledCreditCardsFullPaginatedData:
    (action.type === GET_CREDIT_CARDS_SUCCESS ||
      action.type === GET_CREDIT_CARDS_INIT ||
      action.type === GET_CREDIT_CARDS_FAILURE) &&
    !action.payload.queryParameters.balanceLockedEnabled
      ? state.balanceLockedEnabledCreditCardsFullPaginatedData
      : CreditCardsFullPaginatedDataReducer(
          state.balanceLockedEnabledCreditCardsFullPaginatedData,
          action as CreditCardsFullPaginatedDataActions,
        ),
  usersFullPaginatedData: UsersFullPaginatedDataReducer(
    state.usersFullPaginatedData,
    action as UsersFullPaginatedDataActions,
  ),
  recentUsersFullPaginatedData: RecentUsersFullPaginatedDataReducer(
    state.recentUsersFullPaginatedData,
    action as RecentUsersFullPaginatedDataActions,
  ),
})

const CreateVirtualCardFormReducer = createFlyoutReducer(
  [createVirtualCardRouteProps, createBillPayRouteProps],
  initialState,
  CoreCreateVirtualCardFormReducer,
)

export default CreateVirtualCardFormReducer
